import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import { ReactComponent as ArrowLeft } from '../../assets/arrow-left.svg';
import useAiChatPanel from '../../hooks/useAiChatPanel';
import useWindowSize from '../../hooks/useWindowSize';
import { LoadingButton } from '../Button';
import { Flex } from '../Flex';
import IconButton from '../IconButton';
import Loading from '../Loading';
import { ResizablePanelHorizontal } from '../ResizablePanel';
import {
  MAX_PANEL_WIDTH,
  MEDIUM_PANEL_WIDTH,
  MEDIUM_PANEL_RELATIVE_WIDTH,
} from './constants';
import {
  DrawerStyled,
  Wrapper,
  Header,
  Footer,
  Content,
  CloseIconStyled,
} from './shared';

const HeaderLeft = styled(Flex)`
  gap: 16px;
  align-items: center;
`;

const BackArrowButton = styled(IconButton)`
  width: 16px;
  height: 16px;
  padding: 0;

  svg {
    width: 16px;
    height: 16px;
    path {
      fill: ${({ theme }) => theme.palette.common.gray2};
    }
  }

  &:hover:not(:disabled) svg path {
    fill: ${({ theme }) => theme.palette.common.black};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export type DrawerStep = {
  title: string;
  content: React.ReactNode;
  onStepSubmit?: () => void | boolean | Promise<void | boolean>;
  submitButtonLabel?: string;
  submitDisabled?: boolean;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  steps: DrawerStep[];
  dragHandleOffset?: { x: number; y: number };
  isLoading?: boolean;
  isInitialLoading?: boolean;
  finalSubmitButtonLabel?: string;
  defaultSubmitButtonLabel?: string;
  noPadding?: boolean;
  className?: string;
  currentStep?: number;
  onStepChange?: (step: number) => void;
};

const MultiStepDrawer = ({
  isOpen,
  onClose,
  steps,
  dragHandleOffset,
  isLoading,
  isInitialLoading,
  finalSubmitButtonLabel = 'Submit',
  defaultSubmitButtonLabel = 'Continue',
  noPadding,
  className,
  currentStep: controlledStep,
  onStepChange,
}: Props) => {
  const [panelWidth, setPanelWidth] = useState(0);
  const [internalStep, setInternalStep] = useState(0);
  const { width: windowWidth } = useWindowSize();
  const { isExpanded: isAiChatPanelOpen, width: aiChatPanelWidth } =
    useAiChatPanel();
  const rightOffset = isAiChatPanelOpen ? aiChatPanelWidth : 0;

  const isControlled = controlledStep !== undefined;
  const currentStep = isControlled ? controlledStep : internalStep;

  const setCurrentStep = (step: number) => {
    if (isControlled) {
      onStepChange?.(step);
    } else {
      setInternalStep(step);
    }
  };

  const currentStepData = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  const getSubmitButtonLabel = () => {
    if (currentStepData?.submitButtonLabel) {
      return currentStepData.submitButtonLabel;
    }
    return isLastStep ? finalSubmitButtonLabel : defaultSubmitButtonLabel;
  };

  useEffect(() => {
    if (isOpen && panelWidth) {
      return;
    }
    const relativeWidth = Math.round(windowWidth * MEDIUM_PANEL_RELATIVE_WIDTH);
    const validWidth = Math.max(
      Math.min(relativeWidth, MAX_PANEL_WIDTH),
      MEDIUM_PANEL_WIDTH,
    );
    setPanelWidth(validWidth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowWidth, isOpen]);

  useEffect(() => {
    if (!isOpen && !isControlled) {
      setInternalStep(0);
    }
  }, [isOpen, isControlled]);

  const handleBack = () => {
    if (!isFirstStep) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    const stepAction = currentStepData?.onStepSubmit;

    if (stepAction) {
      const result = await stepAction();
      // If action returns false, don't proceed to next step
      if (result === false) {
        return;
      }
    }

    if (!isLastStep) {
      setCurrentStep(currentStep + 1);
    }
  };

  return (
    <>
      {isOpen && (
        <ResizablePanelHorizontal
          width={panelWidth}
          minWidth={MEDIUM_PANEL_WIDTH}
          maxWidth={MAX_PANEL_WIDTH}
          onDrag={setPanelWidth}
          onMouseEnter={() => null}
          onMouseLeave={() => null}
          direction="right-to-left"
          positionOffset={dragHandleOffset}
          rightOffset={rightOffset}
        />
      )}

      <DrawerStyled
        className={className}
        open={isOpen}
        onClose={onClose}
        $rightOffset={rightOffset}
      >
        <Wrapper
          className="drawer-wrapper"
          $width={panelWidth}
          $rightOffset={rightOffset}
        >
          <Header>
            <HeaderLeft>
              {!isFirstStep && !isInitialLoading && (
                <BackArrowButton onClick={handleBack} disabled={isLoading}>
                  <ArrowLeft />
                </BackArrowButton>
              )}
              <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>
                {isInitialLoading ? 'Loading...' : currentStepData?.title}
              </Typography>
            </HeaderLeft>
            <IconButton onClick={onClose}>
              <CloseIconStyled />
            </IconButton>
          </Header>

          <Content $noPadding={noPadding}>
            {isInitialLoading ? (
              <Loading size={32} />
            ) : (
              currentStepData?.content
            )}
          </Content>

          <Footer>
            <LoadingButton
              type="submit"
              size="large"
              disabled={isInitialLoading || currentStepData?.submitDisabled}
              loading={isLoading}
              onClick={handleSubmit}
            >
              {getSubmitButtonLabel()}
            </LoadingButton>
          </Footer>
        </Wrapper>
      </DrawerStyled>
    </>
  );
};

export default MultiStepDrawer;
